import { type UnifiedJobSearchResult } from '@kalos/kalos-rpc';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@kalos/ui';
import { Link } from '@tanstack/react-router';
import { Briefcase, ClipboardList, MapPin, MoreHorizontal } from 'lucide-react';

import { JOB_STATUS_COLORS } from '../../../../constants';
import { formatDate } from '../../../../tools/helpers';
import { getResultRoute, isResultProject } from './utils';

interface MobileJobCardProps {
  result: UnifiedJobSearchResult;
  jobTypeName?: string;
  jobSubTypeName?: string;
  departmentName?: string;
  canViewCostReport?: boolean;
  onSelectResult?: (result: UnifiedJobSearchResult) => void;
}

export const MobileJobCard = ({
  result,
  jobTypeName,
  jobSubTypeName,
  departmentName,
  canViewCostReport,
  onSelectResult,
}: MobileJobCardProps) => {
  const statusColor = JOB_STATUS_COLORS[result.jobStatus] || 'CCCCCC';
  const formattedDate = result.dateStarted ? formatDate(result.dateStarted) : '-';

  // Build job type / subtype display
  const jobTypeDisplay =
    jobTypeName && jobSubTypeName ? `${jobTypeName} / ${jobSubTypeName}` : jobTypeName || '-';

  // Build address display
  const addressParts = [
    result.propertyAddress,
    result.propertyCity,
    result.propertyState,
    result.propertyZip,
  ].filter(Boolean);
  const fullAddress = addressParts.join(', ');

  // Sector display
  const sectorDisplay = result.isResidential === 1 ? 'Residential' : 'Commercial';

  // Type display (parentId > 0 = project, else service call)
  const typeDisplay = isResultProject(result) ? 'Project' : 'Service Call';

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      {/* Top row: Date and Job # */}
      <div className="mb-2 flex items-center gap-2 text-sm text-gray-500">
        <span>{formattedDate}</span>
        <span>•</span>
        <span>Job #{result.jobNumber}</span>
      </div>

      {/* Customer name */}
      <h3 className="mb-3 text-lg font-semibold text-gray-900">
        {result.customerName || result.businessName || 'Unknown Customer'}
      </h3>

      {/* Address with icon */}
      {fullAddress && (
        <div className="mb-1 flex items-start gap-2 text-sm text-gray-700">
          <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
          <span>{fullAddress}</span>
        </div>
      )}

      {/* Department/Job Type with icon */}
      {(departmentName || jobTypeDisplay !== '-') && (
        <div className="mb-1 flex items-center gap-2 text-sm text-gray-700">
          <Briefcase className="h-4 w-4 flex-shrink-0 text-gray-400" />
          <span>{departmentName ? `${departmentName} - ${jobTypeDisplay}` : jobTypeDisplay}</span>
        </div>
      )}

      {/* Description with icon */}
      {result.description && (
        <div className="mb-3 flex items-center gap-2 text-sm text-gray-700">
          <ClipboardList className="h-4 w-4 flex-shrink-0 text-gray-400" />
          <span className="line-clamp-1">{result.description}</span>
        </div>
      )}

      {/* Status row */}
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <div className="flex items-center gap-1.5">
          <div
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: `#${statusColor}` }}
          />
          <span className="font-medium text-gray-700">{result.jobStatus || 'Unknown'}</span>
        </div>
        <span className="text-gray-400">•</span>
        <span className="text-gray-600">{sectorDisplay}</span>
        <span className="text-gray-400">•</span>
        <span className="text-gray-600">{typeDisplay}</span>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2">
        {onSelectResult ? (
          <Button
            className="bg-primary hover:bg-primary/90 flex-1"
            onClick={() => onSelectResult(result)}
          >
            Select
          </Button>
        ) : (
          <Button variant="outline" className="flex-1 border-gray-300 bg-white" asChild>
            <Link to={getResultRoute(result).to} params={getResultRoute(result).params}>
              View
            </Link>
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="border-gray-300 bg-white">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link
                to={getResultRoute(result).to}
                target="_blank"
                params={getResultRoute(result).params}
              >
                Open in New Tab
              </Link>
            </DropdownMenuItem>
            {canViewCostReport && !isResultProject(result) && (
              <DropdownMenuItem asChild>
                <Link
                  to="/cost-report/$jobNumber"
                  target="_blank"
                  params={{ jobNumber: result.eventId }}
                >
                  View Cost Report
                </Link>
              </DropdownMenuItem>
            )}
            {result.customerId > 0 && (
              <DropdownMenuItem asChild>
                <Link to="/customers/$customerId" params={{ customerId: result.customerId }}>
                  View Customer
                </Link>
              </DropdownMenuItem>
            )}
            {result.propertyId > 0 && (
              <DropdownMenuItem asChild>
                <Link to="/properties/$propertyId" params={{ propertyId: result.propertyId }}>
                  View Property
                </Link>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

// Loading skeleton for mobile cards
export const MobileJobCardSkeleton = () => {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center gap-2">
        <div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
        <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
      </div>
      <div className="mb-3 h-6 w-40 animate-pulse rounded bg-gray-200" />
      <div className="mb-1 h-4 w-full animate-pulse rounded bg-gray-200" />
      <div className="mb-1 h-4 w-3/4 animate-pulse rounded bg-gray-200" />
      <div className="mb-3 h-4 w-1/2 animate-pulse rounded bg-gray-200" />
      <div className="mb-4 flex items-center gap-2">
        <div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
        <div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
        <div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
      </div>
      <div className="flex items-center gap-2">
        <div className="h-9 flex-1 animate-pulse rounded bg-gray-200" />
        <div className="h-9 w-9 animate-pulse rounded bg-gray-200" />
      </div>
    </div>
  );
};
